import { toNumber } from 'lodash';

import { getCartItemsCount, getCartItemsSubtotal, stringToSlug } from '@/utils';
import {
  CartLineItem,
  ISpecial,
  ISpecialProduct,
  PromoCarouselDocumentDataCarouselItemsItem,
  TreezSpecials,
} from '@/types';

const formatSpecial = (
  currentBundleProducts: CartLineItem[],
  currentSpecial: TreezSpecials | undefined,
  products: {
    [key: string]: string[];
  },
  isBogoLoading: boolean,
  cmsDiscountItem?: PromoCarouselDocumentDataCarouselItemsItem | undefined,
): ISpecial => {
  if (!currentSpecial || isBogoLoading) {
    return undefined as unknown as ISpecial;
  }

  const allowDiscountProducts = !currentSpecial?.groupsRequired?.length;

  const dependentSettings = currentSpecial?.discountRules?.find(
    rule => rule?.ruleName === 'GET_COUNT',
  );

  const independentSettings = currentSpecial?.discountRules?.find(
    rule => rule.ruleName === 'BUY_COUNT',
  );

  const dependentProductsRequired = dependentSettings?.ruleValue
    ? toNumber(dependentSettings?.ruleValue)
    : Infinity;

  const totalIndProdsRequired = independentSettings?.ruleValue
    ? toNumber(independentSettings?.ruleValue)
    : 0;

  const specialType = currentSpecial?.method ?? '';

  const depProducts = Object.entries(products).reduce(
    (result, [key, value]: [key: string, value: string[]]) => {
      if (!key.includes('independent')) {
        return [...result, ...value];
      }
      return result;
    },
    [],
  );

  const indProducts = Object.entries(products).reduce(
    (result, [key, value]: [key: string, value: string[]]) => {
      if (key.includes('independent')) {
        return [...result, ...value];
      }
      return result;
    },
    [],
  );

  let independentProductsRequired = totalIndProdsRequired;
  if (allowDiscountProducts) {
    independentProductsRequired = 0;
  }

  const totalProductsRequired =
    dependentProductsRequired + independentProductsRequired;
  let bundleComplete = totalProductsRequired === currentBundleProducts.length;

  const purchaseRequirementRule = getSpecialRuleByName(
    currentSpecial,
    'PURCHASE_REQUIREMENT',
  );
  const purchaseRequirement = purchaseRequirementRule?.ruleValue;

  const buyCountRule = getSpecialRuleByName(currentSpecial, 'BUY_COUNT');
  const buyCount = buyCountRule?.ruleValue
    ? Number(buyCountRule?.ruleValue)
    : undefined;

  if (buyCount) {
    if (purchaseRequirement === 'UNIT_COUNT') {
      bundleComplete = getCartItemsCount(currentBundleProducts) >= buyCount;
    } else if (purchaseRequirement === 'RETAIL_VALUE') {
      const totalRetailValue = getCartItemsSubtotal(currentBundleProducts);
      bundleComplete = totalRetailValue >= buyCount * 100;
    }
  }

  return {
    allowDiscountProducts,
    bundleComplete,
    depProducts: mapBundleProds(depProducts, currentSpecial),
    dependentProductsRequired,
    indProducts: mapBundleProds(indProducts, currentSpecial),
    independentProductsRequired: totalIndProdsRequired,
    method: currentSpecial?.method,
    specialId: currentSpecial?.id,
    specialType,
    specialSlug: stringToSlug(currentSpecial?.title),
    title:
      cmsDiscountItem?.title ||
      currentSpecial?.displayTitle ||
      currentSpecial?.title,
    description: currentSpecial?.description as string,
    posTitle: cmsDiscountItem?.pos_title_name || currentSpecial?.title,
    totalProductsRequired,
    purchaseRequirement,
    buyCount,
  };
};

const getSpecialRuleByName = (special: TreezSpecials, name: string) =>
  special?.discountRules?.find(({ ruleName }) => ruleName === name);

const mapBundleProds = (
  productIds: string[],
  special: TreezSpecials,
): ISpecialProduct[] =>
  productIds.map(prod => ({
    id: prod,
    bundle_id: special.id,
    bundle_title: special?.title ?? '',
    bundle_slug: stringToSlug(special?.title ?? ''),
  }));

/**
 * Bucket key for a deal group result: independent (BUY) groups come first, then
 * dependent (GET). `independentGroupCount` is the number of BUY groups in the deal.
 */
const bogoGroupKey = (
  groupIndex: number,
  independentGroupCount: number,
): string =>
  groupIndex > independentGroupCount - 1
    ? `dependent-${groupIndex}`
    : `independent-${groupIndex}`;

/** Whether the active BOGO cycle still needs BUY units, GET units, or both. */
const getBogoStillNeedSlots = (
  activeBuyUnits: number,
  activeGetUnits: number,
  independentRequired: number,
  dependentRequired: number,
) => {
  const indReq = Math.max(1, independentRequired);
  const depReq = Math.max(1, dependentRequired);

  return {
    depReq,
    indReq,
    stillNeedDependentSlot: activeBuyUnits >= indReq && activeGetUnits < depReq,
    stillNeedIndependentSlot: activeBuyUnits < indReq,
  };
};

/** Maps active cycle counts to ISpecial UI flags (activeBundle, pending GET, processing steps). */
const getBogoFlowFlags = (
  activeBuyUnits: number,
  activeGetUnits: number,
  independentRequired: number,
  dependentRequired: number,
) => {
  const { depReq, indReq } = getBogoStillNeedSlots(
    activeBuyUnits,
    activeGetUnits,
    independentRequired,
    dependentRequired,
  );
  const activeBundleHasPendingGet =
    activeBuyUnits >= indReq && activeGetUnits < depReq;

  return {
    activeBundle: { buyUnits: activeBuyUnits, getUnits: activeGetUnits },
    activeBundleHasPendingGet,
    processingDependents: activeBundleHasPendingGet,
    processingIndependents: activeBuyUnits < indReq,
  };
};

export {
  bogoGroupKey,
  formatSpecial,
  getBogoFlowFlags,
  getBogoStillNeedSlots,
  mapBundleProds,
};
export type { ISpecialProduct };
